import React, { useState, useEffect } from "react";
import DocumentModal from "../modals/DocumentModal";
import UseMyPhoneModal from "../modals/UseMyPhoneModal";
import { supabase } from "../../../lib/supabaseClient";
import { useTranslation } from "react-i18next";
import FieldProvenance from "../FieldProvenance";

// Durée de validité des signed URLs : 1 heure
const SIGNED_URL_EXPIRES_IN = 3600;

// Détecte si une valeur est une URL complète (anciens docs legacy)
// ou un chemin relatif (nouveaux docs)
function isFullUrl(value) {
  return typeof value === "string" && (
    value.startsWith("http://") || value.startsWith("https://")
  );
}

// Extrait le bucket et le chemin depuis une URL Supabase Storage legacy
// ex: https://xxx.supabase.co/storage/v1/object/public/documents/uuid/fichier.pdf
// → { bucket: "documents", path: "uuid/fichier.pdf" }
function parseLegacyUrl(url) {
  try {
    const match = url.match(/\/storage\/v1\/object\/(?:public|sign)\/([^/]+)\/(.+)/);
    if (match) return { bucket: match[1], path: decodeURIComponent(match[2]) };
  } catch {}
  return null;
}

// Génère une signed URL pour un document
// Gère les deux cas : chemin relatif (nouveau) et URL legacy (ancien)
async function getSignedUrl(doc) {
  let bucket = doc.bucket || "documents";
  let path = doc.storagePath;

  // Cas legacy : l'URL complète était stockée dans doc.file
  if (!path && doc.file && isFullUrl(doc.file)) {
    const parsed = parseLegacyUrl(doc.file);
    if (parsed) {
      bucket = parsed.bucket;
      path = parsed.path;
    } else {
      // URL externe (blockchain, etc.) — on l'ouvre directement
      return doc.file;
    }
  }

  if (!path) return null;

  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, SIGNED_URL_EXPIRES_IN);

  if (error) {
    console.error("[DocumentsTab] Erreur signed URL:", error);
    return null;
  }
  return data?.signedUrl || null;
}

// Bouton "Voir" qui génère la signed URL au clic
function ViewDocumentButton({ doc }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    const url = await getSignedUrl(doc);
    setLoading(false);
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      alert(t("artworkDocumentsTab.openError"));
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className="text-sm underline text-blue-600 hover:text-blue-800 disabled:opacity-50"
    >
      {loading ? t("artworkDocumentsTab.loading") : t("artworkDocumentsTab.view")}
    </button>
  );
}

const ArtworkDocumentsTab = ({ formData = {}, setFormData, readOnly = false, canEdit, canReclassify }) => {
  const { t, i18n } = useTranslation();
  // canEdit prime sur readOnly : un expert partagé avec can_edit peut gérer les documents depuis la fiche.
  const isReadOnly = canEdit !== undefined ? !canEdit : readOnly;
  // canReclassify : permet le crayon de reclassification même en lecture visuelle si canEdit=true
  const allowReclassify = canReclassify !== undefined ? canReclassify : !isReadOnly;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [storageDocuments, setStorageDocuments] = useState([]);
  const [lotDocuments, setLotDocuments] = useState([]);        // docs communs du lot
  const [lotInfo, setLotInfo] = useState(null);                // { id, title, reference_image_url }
  const [isLotModalOpen, setIsLotModalOpen] = useState(false); // modal upload → lot
  const [reclassifyDocId, setReclassifyDocId] = useState(null); // id du doc dont le menu est ouvert
  const [showPhone, setShowPhone] = useState(false);           // passerelle QR desktop → mobile
  const [reloadNonce, setReloadNonce] = useState(0);           // force le rechargement des docs storage

  // Charge les documents depuis le sous-dossier Storage {artworkId}/
  useEffect(() => {
    const artworkId = formData?.id;
    if (!artworkId) return;

    const loadStorageDocs = async () => {
      const { data: files, error } = await supabase.storage
        .from("documents")
        .list(artworkId, { limit: 100 });

      if (error || !files || files.length === 0) return;

      // Retire le préfixe UUID — format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx-NomOriginal.pdf
      const cleanFileName = (rawName) => {
        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i;
        return rawName.replace(uuidPattern, "");
      };

      const found = [];
      for (const file of files) {
        if (file.name === ".emptyFolderPlaceholder") continue;

        const displayName = cleanFileName(file.name);
        const nameLower = displayName.toLowerCase();

        let type = "other";
        if (nameLower.includes("facture") || nameLower.includes("invoice")) type = "invoice";
        else if (nameLower.includes("certif")) type = "certificate";
        else if (nameLower.includes("rapport") || nameLower.includes("conservation") || nameLower.includes("constat")) type = "condition_report";
        else if (nameLower.includes("exposition") || nameLower.includes("exhibition")) type = "exhibition";

        found.push({
          id: `storage-${file.name}`,
          name: displayName,
          storagePath: `${artworkId}/${file.name}`,
          bucket: "documents",
          type,
          date: file.created_at || null,
          source: "storage",
        });
      }

      setStorageDocuments(found);
    };

    loadStorageDocs();
  }, [formData?.id, reloadNonce]);

  // Charge les documents communs du lot si l'œuvre appartient à un lot
  useEffect(() => {
    const lotId = formData?.lot_id;
    if (!lotId) return;

    const loadLotData = async () => {
      const { data, error } = await supabase
        .from("lots")
        .select("id, title, documents, reference_image_url")
        .eq("id", lotId)
        .single();

      if (error || !data) return;

      setLotInfo({ id: data.id, title: data.title, reference_image_url: data.reference_image_url });

      const docs = Array.isArray(data.documents) ? data.documents : [];
      setLotDocuments(docs.map((d) => ({ ...d, source: "lot" })));
    };

    loadLotData();
  }, [formData?.lot_id]);

  // Fusionne docs JSON (base) + docs Storage — déduplique par nom de fichier
  const jsonDocuments = formData?.documents || [];
  const jsonFileNames = new Set(
    jsonDocuments.map((d) => {
      const path = d.storagePath || d.file || "";
      return path.split("/").pop().toLowerCase();
    })
  );
  const newStorageDocs = storageDocuments.filter(
    (d) => !jsonFileNames.has(d.name.toLowerCase())
  );
  const documents = [...jsonDocuments, ...newStorageDocs];

  const documentTypeLabels = {
    certificate: t("artworkDocumentsTab.types.certificate"),
    invoice: t("artworkDocumentsTab.types.invoice"),
    condition_report: t("artworkDocumentsTab.types.condition_report"),
    exhibition: t("artworkDocumentsTab.types.exhibition"),
    photo: t("artworkDocumentsTab.types.photo"),
    other: t("artworkDocumentsTab.types.other"),
  };

  const getCertificateDetails = () => {
    const cert = documents.find((doc) => doc.type === "certificate");
    if (!cert) return { exists: false };

    const ref = cert.storagePath || cert.file || "";
    let blockchain = null;
    if (ref.includes("ipfs")) blockchain = "ipfs";
    else if (ref.includes("etherscan") || ref.includes("ethereum")) blockchain = "ethereum";
    else if (ref.includes("arweave")) blockchain = "arweave";
    else if (ref.includes("polygon")) blockchain = "polygon";

    return { exists: true, isBlockchainBased: !!blockchain, doc: cert, blockchain };
  };

  const certificateDetails = getCertificateDetails();

  // Sauvegarde en base (uniquement les docs JSON, pas les docs Storage)
  // artworks_secure est tenté en premier pour les accès experts/can_edit.
  const updateArtworkDocuments = async (artworkId, payload) => {
    const secure = await supabase
      .from("artworks_secure")
      .update(payload)
      .eq("id", artworkId);

    if (!secure.error) return secure;

    console.warn("[DocumentsTab] artworks_secure update failed, fallback artworks:", secure.error);
    return supabase
      .from("artworks")
      .update(payload)
      .eq("id", artworkId);
  };

  const saveDocumentsToDb = async (updatedDocs) => {
    const artworkId = formData?.id;
    if (!artworkId || isReadOnly) return;
    const toSave = updatedDocs.filter((d) => d.source !== "storage");
    console.log("[DocumentsTab] Sauvegarde en base:", toSave);
    const { error } = await updateArtworkDocuments(artworkId, { documents: toSave });
    if (error) console.error("[DocumentsTab] Erreur sauvegarde documents:", error);

    // Le notaire (RPC notaire_dossiers) lit artworks.invoice pour compter les
    // dossiers "avec facture" — sans ce flag, aucune fiche n'apparaît jamais
    // comme documentée même une fois la facture uploadée. artworks_secure n'a
    // pas cette colonne, donc appel séparé direct sur artworks (best-effort :
    // un éditeur avec accès uniquement via artworks_secure ne pourra pas
    // mettre à jour ce flag, mais ça ne doit pas bloquer la sauvegarde des documents).
    const hasInvoice = toSave.some((d) => d.type === "invoice");
    const { error: invoiceError } = await supabase
      .from("artworks")
      .update({ invoice: hasInvoice })
      .eq("id", artworkId);
    if (invoiceError) console.warn("[DocumentsTab] Erreur sync invoice flag:", invoiceError);
  };

  const handleAddDocument = async (newDoc) => {
    if (isReadOnly) return;
    const jsonDocs = formData?.documents || [];
    const updated = [...jsonDocs, newDoc];
    if (setFormData) setFormData({ ...formData, documents: updated });
    await saveDocumentsToDb(updated);
  };

  const handleAddLotDocument = async (newDoc) => {
    if (isReadOnly || !lotInfo?.id) return;
    const updatedLotDocs = [...lotDocuments.map(({ source, ...d }) => d), newDoc];
    const { error } = await supabase
      .from("lots")
      .update({ documents: updatedLotDocs })
      .eq("id", lotInfo.id);
    if (error) {
      console.error("[DocumentsTab] Erreur sauvegarde doc lot:", error);
      return;
    }
    setLotDocuments(updatedLotDocs.map((d) => ({ ...d, source: "lot" })));
  };

  const handleRemoveLotDocument = async (id) => {
    if (isReadOnly || !lotInfo?.id) return;
    const updated = lotDocuments.filter((d) => d.id !== id).map(({ source, ...d }) => d);
    const { error } = await supabase
      .from("lots")
      .update({ documents: updated })
      .eq("id", lotInfo.id);
    if (!error) setLotDocuments(updated.map((d) => ({ ...d, source: "lot" })));
  };

  const handleRemoveDocument = async (id) => {
    if (isReadOnly) return;
    if (!window.confirm(t("artworkDocumentsTab.deleteConfirm"))) return;

    // ── Cas doc Storage (pas encore promu en JSON) ──
    if (id.startsWith("storage-")) {
      const doc = storageDocuments.find((d) => d.id === id);
      if (doc?.storagePath) {
        const { error } = await supabase.storage
          .from("documents")
          .remove([doc.storagePath]);
        if (error) console.error("[DocumentsTab] Erreur suppression Storage:", error);
      }
      setStorageDocuments((prev) => prev.filter((d) => d.id !== id));
      return;
    }

    // ── Cas doc JSON ──
    const jsonDocs = formData?.documents || [];

    const docToRemove =
      jsonDocs.find((d) => d.id === id) || jsonDocs.find((d) => d.name === id);

    // Supprimer aussi le fichier Storage si storagePath présent
    if (docToRemove?.storagePath) {
      await supabase.storage
        .from(docToRemove.bucket || "documents")
        .remove([docToRemove.storagePath]);
    }

    // CORRECTION : filtre par id en priorité, name en fallback — jamais les deux à la fois
    const updated = jsonDocs.filter((d) => {
      if (d.id) return d.id !== id;
      return d.name !== id;
    });

    console.log("[DocumentsTab] Suppression doc:", id, "avant:", jsonDocs.length, "après:", updated.length);

    // Mise à jour immédiate et synchrone — pas besoin de rafraîchir
    if (setFormData) {
      setFormData((prev) => ({ ...prev, documents: updated }));
    }
    await saveDocumentsToDb(updated);
  };

  // Reclassifie un document dans une nouvelle catégorie
  // Les docs "storage" sont promus en JSON au moment de la modification
  const handleReclassify = async (doc, newType) => {
    if (!allowReclassify) return;
    setReclassifyDocId(null);

    if (doc.source === "storage") {
      const jsonDocs = formData?.documents || [];
      const promoted = {
        id: doc.id,
        name: doc.name,
        storagePath: doc.storagePath,
        bucket: doc.bucket,
        type: newType,
        date: doc.date,
      };
      const updated = [...jsonDocs, promoted];
      setStorageDocuments((prev) => prev.filter((d) => d.id !== doc.id));
      if (setFormData) setFormData({ ...formData, documents: updated });
      await saveDocumentsToDb(updated);
      return;
    }

    const jsonDocs = formData?.documents || [];
    const updated = jsonDocs.map((d) =>
      d.id === doc.id || d.name === doc.name ? { ...d, type: newType } : d
    );
    if (setFormData) setFormData({ ...formData, documents: updated });
    await saveDocumentsToDb(updated);
  };

  const groupDocumentsByType = () => {
    const grouped = {};
    Object.keys(documentTypeLabels).forEach((type) => { grouped[type] = []; });
    documents.forEach((doc) => {
      const type = doc.type || "other";
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(doc);
    });
    return grouped;
  };

  const groupedDocuments = groupDocumentsByType();

  const calculateIAScore = (docs = []) => {
    let score = 0;
    const has = (type) => docs.some((doc) => doc.type === type);
    const cert = docs.find((doc) => doc.type === "certificate");
    const certRef = cert?.storagePath || cert?.file || "";
    const isBlockchainCert =
      certRef.includes("ipfs") ||
      certRef.includes("arweave") ||
      certRef.includes("etherscan") ||
      certRef.includes("polygon");

    if (cert) score += 25;
    if (isBlockchainCert) score += 10;
    if (has("condition_report")) score += 20;
    if (has("invoice")) score += 20;
    if (has("exhibition")) score += 15;
    if (has("photo")) score += 10;
    return Math.min(score, 100);
  };

  return (
    // CORRECTION : backdrop ferme le menu uniquement si clic direct sur ce div
    <div
      className="space-y-4 tab-content"
      style={{ minHeight: "400px" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) setReclassifyDocId(null);
      }}
    >
      <h2 className="text-xl font-serif font-semibold mb-4">{t("artworkDocumentsTab.title")}</h2>

      {/* Traçabilité documentaire par champ (§12) */}
      <FieldProvenance artwork={formData} />

      {/* ── Convention de prêt sortant ── */}
      {(formData?.availability === "on_loan" || formData?.loan_status === "on_loan") && (
        <div style={{
          border: "1px solid #3b82f6", background: "#eff6ff",
          borderRadius: 8, padding: "16px 20px", marginBottom: 20,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
            <div style={{ fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: 700, color: "#1e40af" }}>
              ↗ {t("artworkDocumentsTab.loan.title")}
            </div>
            {(formData?.loan_contract_ref || formData?.loan_institution) && (
              <span style={{ fontSize: 11, fontFamily: "system-ui", color: "#6b7280", background: "#dbeafe", padding: "2px 8px", borderRadius: 12 }}>
                {formData.loan_contract_ref || t("artworkDocumentsTab.loan.noReference")}
              </span>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 24px" }}>
            {formData?.loan_institution && (
              <div>
                <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "#9ca3af", fontFamily: "system-ui", marginBottom: 2 }}>{t("artworkDocumentsTab.loan.institution")}</div>
                <div style={{ fontFamily: "Georgia, serif", fontSize: 14, color: "#1a1a1a" }}>{formData.loan_institution}</div>
              </div>
            )}
            {formData?.storage_location_detail && !formData?.loan_institution && (
              <div>
                <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "#9ca3af", fontFamily: "system-ui", marginBottom: 2 }}>{t("artworkDocumentsTab.loan.location")}</div>
                <div style={{ fontFamily: "Georgia, serif", fontSize: 14, color: "#1a1a1a" }}>{formData.storage_location_detail}</div>
              </div>
            )}
            {formData?.loan_contact && (
              <div>
                <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "#9ca3af", fontFamily: "system-ui", marginBottom: 2 }}>{t("artworkDocumentsTab.loan.contact")}</div>
                <div style={{ fontFamily: "Georgia, serif", fontSize: 14, color: "#1a1a1a" }}>{formData.loan_contact}</div>
              </div>
            )}
            {formData?.loan_start_date && (
              <div>
                <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "#9ca3af", fontFamily: "system-ui", marginBottom: 2 }}>{t("artworkDocumentsTab.loan.start")}</div>
                <div style={{ fontFamily: "Georgia, serif", fontSize: 14, color: "#1a1a1a" }}>{new Date(formData.loan_start_date).toLocaleDateString(i18n.language)}</div>
              </div>
            )}
            {formData?.loan_end_date && (
              <div>
                <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "#9ca3af", fontFamily: "system-ui", marginBottom: 2 }}>{t("artworkDocumentsTab.loan.end")}</div>
                <div style={{ fontFamily: "Georgia, serif", fontSize: 14, color: "#1a1a1a" }}>{new Date(formData.loan_end_date).toLocaleDateString(i18n.language)}</div>
              </div>
            )}
            {formData?.loan_conditions && (
              <div style={{ gridColumn: "1 / -1" }}>
                <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "#9ca3af", fontFamily: "system-ui", marginBottom: 2 }}>{t("artworkDocumentsTab.loan.conditions")}</div>
                <div style={{ fontFamily: "Georgia, serif", fontSize: 13, color: "#4b5563", fontStyle: "italic" }}>{formData.loan_conditions}</div>
              </div>
            )}
          </div>
          {!formData?.loan_institution && !formData?.loan_contact && !formData?.loan_start_date && (
            <p style={{ fontFamily: "Georgia, serif", fontSize: 12, color: "#6b7280", fontStyle: "italic", marginTop: 8, marginBottom: 0 }}>
              {t("artworkDocumentsTab.loan.empty")}
            </p>
          )}
        </div>
      )}

      {/* ── Section Documents du lot ── */}
      {lotInfo && (
        <div className="mb-6 border border-[#c9a961]/40 rounded-xl overflow-hidden">
          <div className="bg-[#fdf8ef] px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-[#c9a961]" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.429 9.75 2.25 12l4.179 2.25m0-4.5 5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0 4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0-5.571 3-5.571-3" />
              </svg>
              <span className="text-xs font-semibold uppercase tracking-widest text-[#c9a961]">
                {t("artworkDocumentsTab.lot.title", { title: lotInfo.title })}
              </span>
            </div>
            {!isReadOnly && (
              <button
                type="button"
                onClick={() => setIsLotModalOpen(true)}
                className="text-xs text-[#c9a961] border border-[#c9a961]/50 px-3 py-1 rounded hover:bg-[#c9a961]/10 transition-colors"
              >
                + {t("artworkDocumentsTab.lot.add")}
              </button>
            )}
          </div>

          <div className="p-4 space-y-3">
            {lotInfo.reference_image_url && (
              <div className="flex items-center gap-3 p-3 bg-white border border-gray-100 rounded-lg">
                <img
                  src={lotInfo.reference_image_url}
                  alt={t("artworkDocumentsTab.lot.referenceAlt")}
                  className="w-14 h-14 object-cover rounded"
                />
                <div>
                  <p className="text-sm font-medium text-gray-800">{t("artworkDocumentsTab.lot.referenceTitle")}</p>
                  <p className="text-xs text-gray-400 italic">{t("artworkDocumentsTab.lot.referenceSubtitle")}</p>
                </div>
              </div>
            )}

            {lotDocuments.length === 0 && !lotInfo.reference_image_url ? (
              <p className="text-xs text-gray-400 italic">{t("artworkDocumentsTab.lot.empty")}</p>
            ) : (
              lotDocuments.map((doc) => (
                <div key={doc.id} className="flex justify-between items-center p-3 bg-white border border-gray-100 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{doc.name}</p>
                    <p className="text-xs text-gray-400">
                      {doc.date ? new Date(doc.date).toLocaleDateString(i18n.language) : ""}
                      {doc.type ? ` · ${documentTypeLabels[doc.type] || t("artworkDocumentsTab.types.other")}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <ViewDocumentButton doc={doc} />
                    {!isReadOnly && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleRemoveLotDocument(doc.id); }}
                        className="text-red-400 hover:text-red-600"
                        title={t("artworkDocumentsTab.lot.remove")}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9M4.772 5.79l1.068 13.883a2.25 2.25 0 002.244 2.077h7.832a2.25 2.25 0 002.244-2.077L19.228 5.79m-14.456 0a48.108 48.108 0 013.478-.397m7.5 0a48.667 48.667 0 00-7.5 0m7.5 0V4.874c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0C9.41 2.71 8.5 3.694 8.5 4.874v.519" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
      {/* ── Fin section lot ── */}

      <p className="text-sm text-gray-700 mb-2">
        {t("artworkDocumentsTab.aiScore")} {" "}
        <span className="font-semibold text-blue-600">
          {calculateIAScore(documents)}%
        </span>
      </p>

      {/* Alerts certificat */}
      {!certificateDetails.exists && (
        <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded shadow">
          <p className="text-sm text-orange-800 font-semibold">
            {t("artworkDocumentsTab.certificate.missingTitle")}
          </p>
          <p className="text-sm text-orange-700 mt-2">
            {t("artworkDocumentsTab.certificate.missingBody")}
          </p>
        </div>
      )}

      {certificateDetails.exists && !certificateDetails.isBlockchainBased && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded shadow">
          <p className="text-sm text-yellow-800 font-semibold">{t("artworkDocumentsTab.certificate.classicTitle")}</p>
          <p className="text-sm text-yellow-700 mt-2">
            {t("artworkDocumentsTab.certificate.classicBody")}
          </p>
        </div>
      )}

      {certificateDetails.exists && certificateDetails.isBlockchainBased && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded shadow">
          <p className="text-sm text-green-800 font-semibold">{t("artworkDocumentsTab.certificate.blockchainTitle")}</p>
          <p className="text-sm text-green-700 mt-2">
            {t("artworkDocumentsTab.certificate.blockchainBody", {
              blockchain: certificateDetails.blockchain ? ` (${certificateDetails.blockchain})` : "",
            })}
          </p>
          <div className="mt-3">
            <ViewDocumentButton doc={certificateDetails.doc} />
          </div>
        </div>
      )}

      {/* Liste / état vide */}
      {documents.length === 0 ? (
        <p className="text-gray-500 text-sm italic">
          {t("artworkDocumentsTab.empty")}
        </p>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedDocuments).map(([type, docs]) =>
            docs.length > 0 ? (
              <div key={type} className="space-y-2">
                <h3 className="text-md font-medium text-gray-700">
                  {documentTypeLabels[type] || t("artworkDocumentsTab.types.other")}
                </h3>
                <div className="space-y-2">
                  {docs.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex justify-between items-center p-4 border rounded bg-white"
                    >
                      <div>
                        <p className="font-medium">{doc.name}</p>
                        <p className="text-xs text-gray-500">
                          {t("artworkDocumentsTab.addedOn", { date: doc.date ? new Date(doc.date).toLocaleDateString(i18n.language) : "" })}
                        </p>
                      </div>

                      <div className="flex space-x-2 items-center">
                        <ViewDocumentButton doc={doc} />

                        {/* Bouton reclassifier */}
                        {allowReclassify && (
                          <div className="relative" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              title={t("artworkDocumentsTab.reclassify")}
                              onClick={() =>
                                setReclassifyDocId(reclassifyDocId === doc.id ? null : doc.id)
                              }
                              className="text-gray-400 hover:text-gray-700 p-1 rounded hover:bg-gray-100 transition-colors"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
                              </svg>
                            </button>

                            {/* Menu déroulant catégories */}
                            {reclassifyDocId === doc.id && (
                              <div className="absolute right-0 top-8 z-50 bg-white border border-gray-200 rounded shadow-lg w-52 py-1">
                                <p className="text-xs text-gray-400 px-3 py-1 uppercase tracking-wider border-b border-gray-100 mb-1">
                                  {t("artworkDocumentsTab.reclassify")}
                                </p>
                                {Object.entries(documentTypeLabels).map(([typeKey, typeLabel]) => (
                                  <button
                                    key={typeKey}
                                    type="button"
                                    onClick={() => handleReclassify(doc, typeKey)}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between transition-colors ${
                                      doc.type === typeKey
                                        ? "font-semibold text-black"
                                        : "text-gray-700"
                                    }`}
                                  >
                                    {typeLabel}
                                    {doc.type === typeKey && (
                                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-black shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                                      </svg>
                                    )}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Bouton supprimer — stopPropagation pour ne pas déclencher le backdrop */}
                        {!isReadOnly && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleRemoveDocument(doc.id); }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-5 h-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M14.74 9l-.346 9m-4.788 0L9.26 9M4.772 5.79l1.068 13.883a2.25 2.25 0 002.244 2.077h7.832a2.25 2.25 0 002.244-2.077L19.228 5.79m-14.456 0a48.108 48.108 0 013.478-.397m7.5 0a48.667 48.667 0 00-7.5 0m7.5 0V4.874c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0C9.41 2.71 8.5 3.694 8.5 4.874v.519"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null
          )}
        </div>
      )}

      {/* Bouton ajout — deux voies : cet ordinateur, ou mon téléphone (passerelle QR) */}
      {!isReadOnly && (
        <div className="mt-2 flex items-center gap-4">
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="text-blue-600 text-sm flex items-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4 mr-1"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
            {t("artworkDocumentsTab.add")}
          </button>
          <button
            type="button"
            onClick={() => setShowPhone(true)}
            className="text-[#8a6d1f] text-sm flex items-center hover:underline"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
            </svg>
            {t("usePhone.title")}
          </button>
        </div>
      )}

      {isModalOpen && (
        <DocumentModal
          artworkId={formData?.id}
          onClose={() => setIsModalOpen(false)}
          onAddDocument={handleAddDocument}
        />
      )}

      {isLotModalOpen && lotInfo?.id && (
        <DocumentModal
          artworkId={`lot/${lotInfo.id}`}
          onClose={() => setIsLotModalOpen(false)}
          onAddDocument={(doc) => {
            handleAddLotDocument(doc);
            setIsLotModalOpen(false);
          }}
        />
      )}

      <UseMyPhoneModal
        open={showPhone}
        onClose={() => setShowPhone(false)}
        artworkId={formData?.id}
        actionType="upload_document"
        typeOptions={[
          { value: "invoice", label: t("mobileCapture.doc.invoice") },
          { value: "certificate", label: t("mobileCapture.doc.certificate") },
          { value: "condition", label: t("mobileCapture.doc.condition") },
        ]}
        onCompleted={async () => {
          setReloadNonce((n) => n + 1);
          const artworkId = formData?.id;
          if (!artworkId) return;
          // Re-sync de la colonne documents (le mobile y a inscrit le doc) → visible
          // par l'onglet ET par l'OCR d'enrichissement (OcrEnrichButton lit cette colonne).
          const { data } = await supabase.from("artworks").select("documents").eq("id", artworkId).maybeSingle();
          if (data && setFormData) setFormData((prev) => ({ ...prev, documents: data.documents || [] }));
        }}
      />
    </div>
  );
};

export default ArtworkDocumentsTab;